"use client";

import Button from "@/components/ui/button/Button";
import { Modal } from "@/components/ui/modal/index";
import { handleApiError } from "@/lib/api";
import { rmcClient } from "@/lib/rmcClient";
import React from "react";
import { useDropzone } from "react-dropzone";
import toast from "react-hot-toast";

const EXCEL_ACCEPT = {
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
  "application/vnd.ms-excel": [".xls"],
};

/** Max lines read from sheet for preview (1 header row + data rows). Keeps memory and UI stable for huge files. */
const MAX_PREVIEW_LINES = 880;

function isExcelFile(file) {
  if (!file) return false;
  if (/\.xlsx?$/i.test(file.name)) return true;
  const t = String(file.type || "");
  return (
    t === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
    t === "application/vnd.ms-excel"
  );
}

async function parseExcelForPreview(file) {
  const XLSX = await import("xlsx");
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: "array" });
  const sheetName = wb.SheetNames[0];
  if (!sheetName) {
    throw new Error("The workbook has no sheets.");
  }
  const sheet = wb.Sheets[sheetName];
  if (!sheet["!ref"]) {
    return { sheetName, header: [], body: [], totalRows: 0, previewLinesShown: 0, sheetStartRow: 0 };
  }
  const fullRange = XLSX.utils.decode_range(sheet["!ref"]);
  const totalRowsInSheet = fullRange.e.r - fullRange.s.r + 1;
  const lastPreviewRow = Math.min(fullRange.e.r, fullRange.s.r + MAX_PREVIEW_LINES - 1);
  const clippedSheet = {
    ...sheet,
    "!ref": XLSX.utils.encode_range({
      s: fullRange.s,
      e: { r: lastPreviewRow, c: fullRange.e.c },
    }),
  };
  const raw = XLSX.utils.sheet_to_json(clippedSheet, { header: 1, defval: "", raw: false });
  if (!Array.isArray(raw) || raw.length === 0) {
    return {
      sheetName,
      header: [],
      body: [],
      totalRows: totalRowsInSheet,
      previewLinesShown: 0,
      sheetStartRow: fullRange.s.r,
    };
  }
  const rows = raw.map((r) =>
    Array.isArray(r) ? r.map((c) => (c == null || c === "" ? "" : String(c))) : []
  );
  const maxCol = Math.max(0, ...rows.map((r) => r.length));
  const padded = rows.map((r) => {
    const next = [...r];
    while (next.length < maxCol) next.push("");
    return next;
  });
  const header = padded[0] || [];
  const body = padded.slice(1);
  return {
    sheetName,
    header,
    body,
    totalRows: totalRowsInSheet,
    previewLinesShown: padded.length,
    sheetStartRow: fullRange.s.r,
  };
}

export default function ConcreteChallanImportModal({ isOpen, onClose, onImported }) {
  const [pendingFile, setPendingFile] = React.useState(null);
  const [preview, setPreview] = React.useState(null);
  const [parsing, setParsing] = React.useState(false);
  const [importing, setImporting] = React.useState(false);

  const reset = React.useCallback(() => {
    setPendingFile(null);
    setPreview(null);
    setParsing(false);
    setImporting(false);
  }, []);

  const handleClose = React.useCallback(() => {
    if (importing) return;
    reset();
    onClose();
  }, [importing, onClose, reset]);

  React.useEffect(() => {
    if (!isOpen) reset();
  }, [isOpen, reset]);

  const loadFile = React.useCallback(async (file) => {
    if (!isExcelFile(file)) {
      toast.error("Please choose an Excel file (.xlsx or .xls)");
      return;
    }
    setParsing(true);
    try {
      const data = await parseExcelForPreview(file);
      if (data.totalRows === 0 || data.previewLinesShown === 0) {
        toast.error("That sheet is empty.");
        setPendingFile(null);
        setPreview(null);
        return;
      }
      setPendingFile(file);
      setPreview(data);
    } catch (e) {
      toast.error(e?.message || "Could not read that Excel file.");
      setPendingFile(null);
      setPreview(null);
    } finally {
      setParsing(false);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: EXCEL_ACCEPT,
    maxFiles: 1,
    disabled: parsing || importing,
    onDrop: async (accepted, fileRejections) => {
      if (fileRejections?.length) {
        toast.error("Only .xlsx or .xls files are allowed.");
        return;
      }
      const file = accepted[0];
      if (file) await loadFile(file);
    },
  });

  const runImport = async () => {
    if (!pendingFile) return;
    setImporting(true);
    try {
      await rmcClient.importConcretePurchaseChallans(pendingFile);
      toast.success("Concrete challans imported");
      onImported?.();
      handleClose();
    } catch (err) {
      toast.error(handleApiError(err));
    } finally {
      setImporting(false);
    }
  };

  const previewBody = preview ? preview.body : [];

  const fileRowsNotInPreview =
    preview && preview.totalRows > preview.previewLinesShown
      ? preview.totalRows - preview.previewLinesShown
      : 0;

  const headerCells = React.useMemo(() => {
    if (!preview) return [];
    const fromRow = preview.header.some((c) => String(c).trim() !== "") ? preview.header : null;
    const colCount = Math.max(
      preview.header.length,
      preview.body[0]?.length ?? 0,
      1
    );
    if (fromRow) {
      const padded = [...fromRow];
      while (padded.length < colCount) padded.push("");
      return padded;
    }
    return Array.from({ length: colCount }, (_, i) => `Column ${i + 1}`);
  }, [preview]);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} className="max-w-5xl w-full" showCloseButton={!importing}>
      <div className="flex max-h-[calc(90vh-5rem)] flex-col">
        <h2 className="pr-10 text-lg font-semibold text-white">Import concrete challans</h2>
        <p className="mt-1 text-sm text-gray-400">
          Upload an Excel file, review the first rows, then import to the server.
        </p>

        {!preview ? (
          <div className="mt-6 shrink-0">
            <div
              {...getRootProps()}
              className={`cursor-pointer rounded-xl border border-dashed p-8 text-center transition ${
                isDragActive
                  ? "border-emerald-500/70 bg-emerald-500/10"
                  : "border-gray-600 bg-gray-900/50 hover:border-gray-500"
              } ${parsing || importing ? "pointer-events-none opacity-60" : ""}`}
            >
              <input {...getInputProps()} />
              {parsing ? (
                <p className="text-sm text-gray-300">Reading workbook…</p>
              ) : (
                <>
                  <p className="text-sm font-medium text-white">
                    {isDragActive ? "Drop the file here" : "Drag & drop your Excel file here"}
                  </p>
                  <p className="mt-2 text-xs text-gray-400">or click to browse — .xlsx and .xls only</p>
                </>
              )}
            </div>
          </div>
        ) : (
          <div className="mt-4 flex min-h-0 flex-1 flex-col gap-3">
            <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 text-sm">
              <div className="min-w-0 text-gray-300">
                <span className="font-medium text-white">{pendingFile?.name}</span>
                <span className="text-gray-500"> · </span>
                <span className="text-gray-400">
                  Sheet &quot;{preview.sheetName}&quot; · {preview.totalRows.toLocaleString()} row
                  {preview.totalRows !== 1 ? "s" : ""} in file
                </span>
                {fileRowsNotInPreview > 0 ? (
                  <span className="mt-1 block text-xs text-amber-400/90">
                    Preview loads the first {preview.previewLinesShown.toLocaleString()} rows only (
                    {fileRowsNotInPreview.toLocaleString()} more in file). Full file is still sent on Import.
                  </span>
                ) : null}
              </div>
              <Button
                size="sm"
                variant="outline"
                disabled={importing}
                className="shrink-0"
                onClick={() => {
                  setPendingFile(null);
                  setPreview(null);
                }}
              >
                Choose different file
              </Button>
            </div>

            <div className="min-h-0 flex-1 rounded-lg border border-gray-800 bg-gray-950/30">
              <div className="max-h-[min(48vh,420px)] overflow-auto overscroll-contain sm:max-h-[min(52vh,480px)]">
                <table className="min-w-max border-collapse text-left text-xs text-gray-200">
                  <thead className="sticky top-0 z-10 border-b border-gray-800 bg-gray-900 shadow-[0_1px_0_0_rgb(31_41_55)]">
                    <tr>
                      <th className="sticky left-0 z-30 w-10 min-w-10 border-b border-r border-gray-800 bg-gray-900 px-1.5 py-2 text-center font-semibold text-gray-400 shadow-[1px_0_0_0_rgb(31_41_55)]">
                        #
                      </th>
                      {headerCells.map((cell, i) => (
                        <th
                          key={i}
                          className="min-w-28 max-w-56 border-b border-gray-800 px-2 py-2 font-semibold text-white"
                        >
                          <span className="line-clamp-2 wrap-break-word whitespace-normal" title={String(cell)}>
                            {cell || `Column ${i + 1}`}
                          </span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewBody.length === 0 ? (
                      <tr>
                        <td
                          colSpan={Math.max(1, headerCells.length) + 1}
                          className="px-2 py-4 text-center text-gray-500"
                        >
                          No data rows (header only).
                        </td>
                      </tr>
                    ) : (
                      previewBody.map((row, ri) => {
                        const sheetRow1Based = preview.sheetStartRow + ri + 2;
                        const zebra = ri % 2 === 0 ? "bg-gray-950/55" : "bg-gray-950/25";
                        return (
                          <tr key={ri} className={`border-b border-gray-800/80 ${zebra}`}>
                            <td
                              className={`sticky left-0 z-5 border-r border-gray-800 px-1.5 py-1.5 text-center tabular-nums text-gray-500 ${zebra}`}
                              title={`Sheet row ${sheetRow1Based}`}
                            >
                              {sheetRow1Based}
                            </td>
                            {row.map((cell, ci) => (
                              <td
                                key={ci}
                                className="min-w-28 max-w-56 px-2 py-1.5 align-top text-gray-300"
                              >
                                <span className="line-clamp-3 wrap-break-word" title={cell}>
                                  {cell}
                                </span>
                              </td>
                            ))}
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <p className="shrink-0 text-[11px] text-gray-500">
              Scroll vertically and horizontally to scan the preview. Import sends the complete workbook.
            </p>

            <div className="flex shrink-0 flex-wrap justify-end gap-2 border-t border-gray-800 pt-3">
              <Button size="sm" variant="outline" disabled={importing} onClick={handleClose}>
                Cancel
              </Button>
              <Button size="sm" disabled={importing || !pendingFile} onClick={runImport}>
                {importing ? "Importing…" : "Import data"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
